import { buildDom, buildFromTemplate, DOMElement, elOf } from './DomBuilder';

interface SuggestDocument {
    link: string;
    type: string;
    title: string;
    content: string;
    group: string;
    previewImage: string;
}

type SuggestTerms = { [index: string]: number };

interface SuggestResponse {
    status?: false;
    didSecondSearch: boolean;
    suggestion: string;
    suggestions: SuggestTerms;
    documents: SuggestDocument[];
    pages: SuggestDocument[];
    tx_eeproducts_domain_model_download: SuggestDocument[];
    tx_eeproducts_domain_model_product: SuggestDocument[];
    tx_eeproducts_domain_model_productrange: SuggestDocument[];
    tx_eeproducts_domain_model_indicator: SuggestDocument[];
    tx_eeproducts_domain_model_application: SuggestDocument[];
    tx_eeproducts_domain_model_segments: SuggestDocument[];
}

export class Autocomplete {
    readonly #container: HTMLElement;
    readonly #form: HTMLFormElement;
    readonly #input: HTMLInputElement;
    readonly #url: string;
    readonly #listContainer: HTMLElement;
    readonly maxItems: number = 3;
    readonly contactPageUrl: string;

    #inputTO: number = 0;

    constructor(container: HTMLElement, form: HTMLFormElement, input: HTMLInputElement) {
        this.#container = container;
        this.#form = form;
        this.#input = input;
        this.#url = this.#form.dataset.suggest as string;
        this.#listContainer = document.createElement('div');
        this.#container.appendChild(this.#listContainer);
        this.contactPageUrl = this.#container.dataset.contactUrl ?? '';

        this.#input.addEventListener('focus', (): void => {
            this.activate();
        });
    }

    readonly #onInput = (): void => {
        this.handle();
    };

    readonly #onKey = (e: KeyboardEvent): void => {
        switch (e.key) {
            case 'ArrowDown':
                this.moveCursor(e, 1);
                break;
            case 'ArrowUp':
                this.moveCursor(e, -1);
                break;
            case 'Escape':
                this.deactivate();
                break;
            case 'Enter':
                const active = this.#listContainer.querySelector<HTMLElement>('a.hover');
                if (active != null) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    if (active.dataset.autocompleteUrl) {
                        location.href = active.dataset.autocompleteUrl;
                    } else if (active.dataset.autocompleteTerm) {
                        this.select(active.dataset.autocompleteTerm);
                    }
                }
                break;
        }
    };

    readonly #onClick = (e: MouseEvent): void => {
        const target = e.target as HTMLElement;
        if (target.dataset.autocompleteTerm) {
            this.select(target.dataset.autocompleteTerm);
            e.preventDefault();
        } else if (target.dataset.autocompleteUrl) {
            location.href = target.dataset.autocompleteUrl;
        }
        e.stopPropagation();
    };

    readonly #onDeactivate = (e: Event): void => {
        let p: ParentNode | null = e.target as ParentNode;
        if (!p) return;

        while (p) {
            if (this.#container.isSameNode(p)) return;
            p = p.parentNode;
        }
        this.deactivate();
    };

    readonly #onHover = (e: MouseEvent): void => {
        const el = e.target as HTMLAnchorElement;
        if (el.tagName == 'A') {
            this.#listContainer.querySelectorAll('a.hover').forEach((el: Element) => el.classList.remove('hover'));
            el.classList.add('hover');
        }
    };

    private activate() {
        this.#container.appendChild(this.#listContainer);
        this.#input.addEventListener('input', this.#onInput);
        this.#input.addEventListener('keydown', this.#onKey);
        this.#container.addEventListener('click', this.#onClick);
        this.#container.addEventListener('mouseover', this.#onHover);
        window.addEventListener('click', this.#onDeactivate);
        window.addEventListener('touchend', this.#onDeactivate);
        this.handle();
    }

    private deactivate() {
        this.#container.removeChild(this.#listContainer);
        this.#input.removeEventListener('input', this.#onInput);
        this.#input.removeEventListener('keydown', this.#onKey);
        this.#container.removeEventListener('click', this.#onClick);
        this.#container.removeEventListener('mouseover', this.#onHover);
        window.removeEventListener('click', this.#onDeactivate);
        window.removeEventListener('touchend', this.#onDeactivate);
        this.#input.blur();
    }

    private renderBox(...elements: DOMElement[]): void {
        while (this.#listContainer.firstChild) {
            this.#listContainer.removeChild(this.#listContainer.firstChild);
        }
        if (elements.length > 0) {
            buildDom(this.#listContainer, elements);
        }
    }

    private handle(): void {
        if (this.#inputTO) clearTimeout(this.#inputTO);

        this.#inputTO = setTimeout(async () => {
            if (this.#input.value.trim() == '') {
                this.renderBox();
                return;
            }

            const url = this.#url + '?tx_solr[queryString]=' + encodeURIComponent(this.#input.value);
            const resp: SuggestResponse = await (await fetch(url)).json();

            if (resp?.status === false) {
                this.renderBox();
                return;
            }

            const listsCol1: DOMElement[] = [];
            const listsCol2: DOMElement[] = [];
            const listsCol3: DOMElement[] = [];

            const suggests = Object.keys(resp.suggestions);
            if (suggests.length > 0) {
                const suggestEls: DOMElement[] = [];
                suggestEls.push(elOf('li', 'search-type-header', TYPO3['lang']['js.suggest'] ?? null));
                for (const term of suggests) {
                    const a = elOf('a', { 'data-autocomplete-term': term, href: '#', class: 'search-link' }, term);
                    suggestEls.push(elOf('li', null, [a]));
                }
                listsCol1.push(elOf('ul', 'search-type-suggest', suggestEls));
            }

            const suggestionGroups = [
                {
                    data: resp.tx_eeproducts_domain_model_product,
                    langKey: 'js.products',
                    cssClass: 'search-type-product',
                },
                {
                    data: resp.tx_eeproducts_domain_model_productrange,
                    langKey: 'js.productranges',
                    cssClass: 'search-type-productranges',
                },
                {
                    data: resp.tx_eeproducts_domain_model_download,
                    langKey: 'js.downloads',
                    cssClass: 'search-type-downloads',
                },
                {
                    data: resp.pages,
                    langKey: 'js.pages',
                    cssClass: 'search-type-pages',
                },
                {
                    data: resp.tx_eeproducts_domain_model_indicator,
                    langKey: 'js.indicators',
                    cssClass: 'search-type-indicators',
                },
                {
                    data: resp.tx_eeproducts_domain_model_application,
                    langKey: 'js.applications',
                    cssClass: 'search-type-applications',
                },
                {
                    data: resp.tx_eeproducts_domain_model_segments,
                    langKey: 'js.segments',
                    cssClass: 'search-type-segments',
                },
            ];

            suggestionGroups.forEach((group) => {
                this.addListIfPresent(listsCol2, group.data, group.langKey, group.cssClass);
            });

            if (!listsCol2.length) {
                const lang = TYPO3['lang']['js.no-results'] as string;
                const contactLang = TYPO3['lang']['js.no-results-contact'] as string;
                const searchTerm = this.#input.value.trim();

                listsCol2.push(
                    buildFromTemplate(
                        lang,
                        {
                            '#SEARCHTERM#': elOf('b', null, "'" + searchTerm + "'"),
                            '#CONTACT#': elOf('a', { href: this.contactPageUrl }, contactLang),
                        },
                        'p',
                        'bodytext mb-fg'
                    )
                );
            } else {
                listsCol3.push(
                    elOf(
                        'button',
                        { class: 'btn btn-primary', type: 'submit', form: this.#form.id ?? '' },
                        TYPO3['lang']['js.search-submit-button.label']
                    )
                );
            }

            if (listsCol1.length > 0 || listsCol2.length > 0) {
                const col1 = elOf('div', 'tx-solr-suggest-box__suggestions', listsCol1);
                const col2 = elOf('div', 'tx-solr-suggest-box__results', listsCol2);
                const col3 = elOf('div', 'tx-solr-suggest-box__show-all', listsCol3);
                const suggestBoxInner = elOf('div', 'tx-solr-suggest-box__inner', [col1, col2, col3]);
                this.renderBox(elOf('div', 'tx-solr-suggest-box', [suggestBoxInner]));
            } else {
                this.renderBox();
            }
        }, 300);
    }

    private addListIfPresent(
        targetArray: DOMElement[],
        data: SuggestDocument[],
        langKey: string,
        cssClass: string
    ): void {
        const list = this.createDocumentList(data, langKey, cssClass);
        if (list) {
            targetArray.push(list);
        }
    }

    private createDocumentList(items: SuggestDocument[], langKey: string, cssClass: string): DOMElement | null {
        if (!items || items.length === 0) {
            return null;
        }

        const elements: DOMElement[] = [];
        elements.push(elOf('li', 'search-type-header', TYPO3['lang'][langKey] ?? null));

        items.slice(0, this.maxItems).forEach((el: SuggestDocument): void => {
            const title = this.truncateTitle(el.title);
            let anchorContent: DOMElement[] | string = title;

            if (el.previewImage) {
                const img = elOf('img', { src: el.previewImage, class: 'suggest-image' }, []);
                const text = elOf('span', 'suggest-text', title);
                anchorContent = [img, text];
            }

            const a = elOf(
                'a',
                { 'data-autocomplete-url': el.link, href: el.link, title: el.title, class: 'search-link' },
                anchorContent
            );
            elements.push(elOf('li', null, [a]));
        });

        return elOf('ul', cssClass, elements);
    }

    private truncateTitle(title: string): string {
        return title; // title.length > 30 ? title.substring(0, 30) + ' …' : title;
    }

    static create(container: HTMLElement, form: HTMLFormElement, input: HTMLInputElement) {
        return new Autocomplete(container, form, input);
    }

    private select(term: string) {
        this.#input.value = term;
        this.deactivate();
        this.#form.submit();
    }

    private moveCursor(e: KeyboardEvent, offset: number) {
        const elements = this.#listContainer.querySelectorAll('a');
        let active = -1;

        for (const [i, el] of elements.entries()) {
            if (el.classList.contains('hover')) {
                active = i;
                el.classList.remove('hover');
            }
        }

        active += offset;
        if (active >= elements.length) {
            active = 0;
        } else if (active < 0) {
            active = elements.length - 1;
        }

        elements[active].classList.add('hover');

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    }
}
